import { Component, Input, Output, EventEmitter, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface SegmentedOption {
  value: string;
  label: string;
  icon?: string;
  accent?: string;
  /** A stub segment does not commit on click; the host handles revert + messaging. */
  stub?: boolean;
}

@Component({
  selector: 'app-segmented-control',
  templateUrl: './segmented-control.component.html',
  styleUrls: ['./segmented-control.component.css'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SegmentedControlComponent),
      multi: true
    }
  ]
})
export class SegmentedControlComponent implements ControlValueAccessor {
  @Input() options: SegmentedOption[] = [];
  @Input() disabled = false;
  /** Emitted when a stub segment is clicked (caller shows the message / reverts). */
  @Output() stubSelected = new EventEmitter<SegmentedOption>();
  /** Emitted on any committed change. */
  @Output() changed = new EventEmitter<string>();

  value: string | null = null;

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  select(option: SegmentedOption): void {
    if (this.disabled) {
      return;
    }
    if (option.stub) {
      this.stubSelected.emit(option);
      return;
    }
    if (option.value === this.value) {
      return;
    }
    this.value = option.value;
    this.onChange(this.value);
    this.onTouched();
    this.changed.emit(this.value);
  }

  isSelected(option: SegmentedOption): boolean {
    return !option.stub && option.value === this.value;
  }

  segmentStyle(option: SegmentedOption): { [key: string]: string } {
    if (this.isSelected(option) && option.accent) {
      return { color: option.accent };
    }
    return {};
  }

  writeValue(value: string | null): void {
    this.value = value;
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
